import React, { HtmlHTMLAttributes, useRef, useState } from "react";
import clsx from "clsx";

import { ForecastNewProductContentProps } from "./types/ForecastNewProductStepForm";
import Button from "components/common/Button";
import TextInput from "components/common/InputField/TextInput";
import SelectInput from "components/common/InputField/SelectInput";
import { getTextFieldProps } from "helpers/getTextFieldProps";
import TextareaInput from "components/common/InputField/TextareaInput";
import { getSalesChannelIcon } from "utils/get-sales-channel-icon";
import GrowthRateSlider from "components/common/GrowthRateSlider";
import useGetSalesChannel from "hooks/Dashboard/useGetSalesChannel";
import ProductSelectInput from "components/common/ProductSelectInput";
import Paper from "components/common/Paper";
import DateRangeField from "components/DateRangeField";

type ForecastNewProductPlanAddEditProps = { isEdit?: boolean } & ForecastNewProductContentProps &
    HtmlHTMLAttributes<HTMLDivElement>;

export default function ForecastNewProductPlanAddEdit(props: ForecastNewProductPlanAddEditProps) {
    const { stepper, formik, isEdit, ...rest } = props;

    const similarProducts = formik.values.similarProducts;
    const getSalesChannelsQuery = useGetSalesChannel();

    const [showMoreProduct, setShowMoreProduct] = useState<number[]>([1]);

    const handleAddSimilarProduct = (index: number) => {
        formik.setFieldValue("similarProducts", [
            ...similarProducts,
            { channel: "", productId: "", forecastPeriod: "" },
        ]);
        setShowMoreProduct([...showMoreProduct, index]);
    };

    const handleRemoveSimilarProduct = (index: number) => {
        formik.setFieldValue(
            "similarProducts",
            similarProducts.filter((_, i) => i !== index)
        );
        setShowMoreProduct(showMoreProduct.filter((i) => i !== index));
    };

    const handleShowMoreProduct = (index: number) => {
        setShowMoreProduct(
            showMoreProduct.includes(index)
                ? showMoreProduct.filter((i) => i !== index)
                : [...showMoreProduct, index]
        );
    };

    return (
        <div {...rest}>
            <div className="grid grid-cols-3 gap-2">
                <SelectInput
                    placeholder="Select Channel"
                    handleChange={(_, value) => {
                        formik.setFieldValue(`productChannel`, value);
                    }}
                    isRequired
                    value={formik.values?.productChannel}
                    dropdownOptions={[
                        ...getSalesChannelsQuery?.data?.map((channel) => ({
                            label: channel,
                            value: channel,
                            icon: getSalesChannelIcon(channel) || null,
                        })),
                    ]}
                    className="!w-full"
                />
                <ProductSelectInput
                    value={formik.values?.productId}
                    handleChange={(_, value) => {
                        formik.setFieldValue(`productId`, value);
                    }}
                    className="!w-full"
                    required
                />

                <TextInput
                    isRequired
                    type="date"
                    placeholder="EnterLaunch Date"
                    {...getTextFieldProps(formik, "launchDate")}
                    value={formik.values.launchDate}
                    required
                    className="w-full"
                />
            </div>

            <div className="space-y-4">
                <div className="flex justify-between items-center gap-2 mt-[24px]">
                    <hr className="border-[#E2E8F0] w-full" />
                    <p className="text-[14px] text-[#64748B] font-[500] font-rocGroteskMedium leading-[20px] w-full text-center whitespace-nowrap">
                        Adjust the expected weekly growth rate for new this product
                    </p>
                    <hr className="border-[#E2E8F0] w-full" />
                </div>

                <div>
                    <GrowthRateSlider
                        value={formik.values.projectedGrowthRate}
                        onChange={(e) => {
                            formik.setFieldValue("projectedGrowthRate", parseInt(e.target.value));
                        }}
                        endLabel="Weekly"
                    />
                </div>
            </div>

            <div className="space-y-4">
                <div className="flex justify-between items-center gap-2 mt-8">
                    <hr className="border-[#E2E8F0] w-full" />
                    <p className="text-[14px] text-[#64748B] font-[500] font-rocGroteskMedium leading-[20px] w-full text-center whitespace-nowrap">
                        Describe product launch & forecast goals for this new product{" "}
                    </p>
                    <hr className="border-[#E2E8F0] w-full" />
                </div>

                <TextareaInput
                    inputPlaceholder="Add context about this new product launch—marketing plans, expected growth rate, how we should apply historical data, or your sales target (e.g., 'Sales target: 6,000 units over 6 months...')."
                    label="Additional launch details"
                    rows={2}
                    inputClassName="placeholder:text-[#94A3B8] !font-normal !font-rocGroteskRegular !text-[14px]"
                    {...getTextFieldProps(formik, "additionalDetails")}
                    required
                    className="w-full !text-[14px]"
                />

                <div className="flex items-center gap-[8px]">
                    <div className="w-[15px] h-[15px] rounded-full bg-[#D0D4D7] flex justify-center items-center">
                        <i className="ri-robot-fill text-[#0A141C] text-[10px]"></i>
                    </div>
                    <p className="!text-[14px] font-[500px] text-[#64748B] font-rocGroteskMedium">
                        Our AI will take this into account when forecasting for the new product.
                    </p>
                </div>
            </div>

            <div>
                <div className="flex justify-between items-center gap-2 mt-6">
                    <hr className="border-[#E2E8F0] w-full" />
                    <p className="text-[14px] text-[#64748B] font-[500] font-rocGroteskMedium leading-[20px] w-full text-center whitespace-nowrap">
                        Select similar existing product data to forecast this new product.
                    </p>
                    <hr className="border-[#E2E8F0] w-full" />
                </div>

                <div className="mt-4 space-y-3">
                    {similarProducts.map((_, index) => (
                        <Paper
                            className={clsx(
                                "cursor-pointer",
                                showMoreProduct.includes(index) && "bg-[#F1F5F9]"
                            )}
                        >
                            <div
                                className="flex items-center justify-between"
                                onClick={() => handleShowMoreProduct(index)}
                            >
                                <p className="text-[14px] text-[#042821] font-[500] font-rocGroteskMedium leading-[20px]">
                                    Product {index + 1}
                                </p>
                                <div className="flex items-center gap-2">
                                    <button
                                        className={clsx("cursor-pointer", index === 0 && "hidden")}
                                        onClick={() => handleRemoveSimilarProduct(index)}
                                    >
                                        <i className="ri-delete-bin-line text-[#E4281D] cursor-pointer"></i>
                                    </button>

                                    <button
                                        className={clsx("cursor-pointer")}
                                        onClick={() => handleShowMoreProduct(index)}
                                    >
                                        <i
                                            className={clsx(
                                                "cursor-pointer",
                                                showMoreProduct.includes(index)
                                                    ? "ri-arrow-up-s-line text-[#042821]"
                                                    : "ri-arrow-down-s-line text-[#042821]"
                                            )}
                                        ></i>
                                    </button>
                                </div>{" "}
                            </div>

                            <div
                                key={index}
                                className={clsx(
                                    "mt-1",
                                    showMoreProduct.includes(index) && "hidden"
                                )}
                            >
                                <div className="grid grid-cols-3 gap-2">
                                    <SelectInput
                                        placeholder="Select Channel"
                                        handleChange={(_, value) => {
                                            formik.setFieldValue(
                                                `similarProducts.${index}.channel`,
                                                value
                                            );
                                        }}
                                        value={formik.values?.similarProducts[index].channel}
                                        dropdownOptions={[
                                            ...getSalesChannelsQuery?.data?.map((channel) => ({
                                                label: channel,
                                                value: channel,
                                                icon: getSalesChannelIcon(channel) || null,
                                            })),
                                        ]}
                                        className="!w-full"
                                        isRequired
                                    />

                                    <div className="w-full">
                                        <ProductSelectInput
                                            value={formik.values?.similarProducts[index].productId}
                                            handleChange={(_, value) => {
                                                formik.setFieldValue(
                                                    `similarProducts.${index}.productId`,
                                                    value
                                                );
                                            }}
                                            className="!w-full"
                                            required
                                        />
                                    </div>

                                    <DateRangeField
                                        value={[
                                            ...(formik.values?.similarProducts[index]
                                                ?.forecastStartDate
                                                ? [
                                                      formik.values?.similarProducts[index]
                                                          ?.forecastStartDate,
                                                  ]
                                                : []),
                                            ...(formik.values?.similarProducts[index]
                                                ?.forecastEndDate
                                                ? [
                                                      formik.values?.similarProducts[index]
                                                          ?.forecastEndDate,
                                                  ]
                                                : []),
                                        ]}
                                        onChange={(e) => {
                                            formik.setFieldValue(
                                                `similarProducts.${index}.forecastStartDate`,
                                                new Date(e[0]).toISOString()
                                            );
                                            formik.setFieldValue(
                                                `similarProducts.${index}.forecastEndDate`,
                                                new Date(e[1]).toISOString()
                                            );
                                            console.log("date range", e);
                                        }}
                                        toolTipController={{
                                            content:
                                                "Choose the historical time range from the existing product to use as a reference for forecasting this new product.",
                                            className:
                                                "!w-[500px] !max-w-[374px] !p-[12px] !rounded-[8px]",
                                            place: "top",
                                            style: { maxWidth: "250px", zIndex: 9999 },
                                        }}
                                        required
                                    />
                                </div>

                                <div
                                    className={clsx(
                                        isEdit ? "rid-cols-1" : "grid-cols-1",
                                        "mt-2 grid gap-2"
                                    )}
                                >
                                    <div>
                                        <TextInput
                                            label="How would you describe its price point at the time of its launch, relative to other products in its category?"
                                            inputPlaceholder="Enter answer"
                                            labelClassName="!text-[14px]"
                                            {...getTextFieldProps(
                                                formik,
                                                `similarProducts.${index}.relativePriceTier`
                                            )}
                                            required
                                            className={clsx("font-[14px] !w-full")}
                                        />
                                    </div>

                                    <div>
                                        <TextInput
                                            inputPlaceholder="Enter answer"
                                            label="Describe the launch campaign and initial marketing push"
                                            labelClassName="!text-[14px]"
                                            {...getTextFieldProps(
                                                formik,
                                                `similarProducts.${index}.marketingAggressiveness`
                                            )}
                                            required
                                            className="font-[14px] !w-full"
                                        />
                                    </div>

                                    <div>
                                        <TextInput
                                            inputPlaceholder="Enter answer"
                                            labelClassName="!text-[14px]"
                                            label="Who was the primary target audience for the product"
                                            {...getTextFieldProps(
                                                formik,
                                                `similarProducts.${index}.targetAudience`
                                            )}
                                            required
                                            className="font-[14px] !w-full"
                                        />
                                    </div>
                                </div>
                            </div>
                        </Paper>
                    ))}

                    <Button
                        btnText="Add more products"
                        type="submit"
                        onClick={() => handleAddSimilarProduct(similarProducts.length)}
                        icon={<i className="ri-add-line text-[16px]"></i>}
                        btnTextClassName="!underline"
                        btnClassname="!bg-transparent !text-[#6562E5]  mt-4 cursor-pointer !text-[13px] !px-[0px] !py-[12px] rounded-[5px] !h-[42px] w-min !whitespace-nowrap"
                    />
                </div>
            </div>
        </div>
    );
}
